import { useState, type FormEvent } from 'react'

import { ApiError, api } from '../lib/api'
import { useSession } from '../lib/session'
import { Button } from '../components/ui/button'
import { Field, Input } from '../components/ui/field'

/**
 * Sign in.
 *
 * No "create an account" and no password reset, deliberately: operators are created by a
 * SUPER_ADMIN and never self-register. A link to either would be copy promising a feature that
 * does not exist.
 *
 * In production this sits behind Cloudflare Access as well. This layer exists for a different
 * reason — **per-operator identity**, because §57 requires audit entries to record *who*, and a
 * shared credential behind a network gate cannot answer that.
 */
export function LoginScreen() {
  const { refresh } = useSession()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setError(null)

    try {
      await api.post('/auth/login', { email, password })
      await refresh()
    } catch (err) {
      // The API returns one message for "no such user" and for "wrong password" so the endpoint
      // cannot be used to enumerate operators. Shown verbatim rather than guessing which it was.
      setError(err instanceof ApiError ? err.message : 'Could not sign in')
      setBusy(false)
    }
  }

  return (
    <div className="bg-bg flex h-full items-center justify-center p-6">
      <div className="w-full max-w-[360px]">
        <div className="mb-6 flex items-center gap-2.5">
          <div className="bg-accent h-[26px] w-[26px] shrink-0 rounded-[7px]" aria-hidden />
          <div>
            <h1 className="text-text text-[15px] font-semibold tracking-[-0.01em]">Mail Room</h1>
            <p className="text-faint text-[11.5px]">Outreach control plane · internal access</p>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-panel border-line rounded-[11px] border p-5"
        >
          <div className="space-y-4">
            <Field label="Email">
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              )}
            </Field>

            <Field label="Password">
              {(props) => (
                <Input
                  {...props}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              )}
            </Field>
          </div>

          {error && (
            <p
              role="alert"
              className="bg-badbg text-bad mt-4 rounded-[7px] px-3 py-2 text-[12px]"
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" disabled={busy} className="mt-5 w-full">
            {busy ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>
      </div>
    </div>
  )
}
